import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { RefreshCcw } from 'lucide-react';
import './LegalApp.css';
import Navbar from './Navbar';
import Footer from './Footer';
import BarChart from './BarChart';
import { getLastNDays, countByDate, countUniqueMobileByDate, uniqueMobiles } from '../utils/adminStats';

const STATS_WINDOW_DAYS = 7;

const downloadMobileList = (mobiles, filename) => {
  const content = mobiles.join('\n');
  const blob = new Blob([content], { type: 'text/plain' });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
};

const AdminPage = ({ API_URL }) => {
  const navigate = useNavigate();
  const role = localStorage.getItem('role');

  const [newUsersList, setNewUsersList] = useState([]);
  const [activeUsersList, setActiveUsersList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (role !== 'admin') {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const fetchStats = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const days = getLastNDays(STATS_WINDOW_DAYS);
      const startStr = days[0].key;
      const endStr = days[days.length - 1].key;
      const jwt = localStorage.getItem('jwtToken');
      const headers = { Authorization: `Bearer ${jwt}` };

      const [newUsersRes, activeUsersRes] = await Promise.all([
        axios.get(`${API_URL}/user/list/${startStr}/${endStr}`, { headers }),
        axios.get(`${API_URL}/user/log/${startStr}/${endStr}`, { headers }),
      ]);

      setNewUsersList(newUsersRes.data || []);
      setActiveUsersList(activeUsersRes.data || []);
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      setErrorMessage('An error occurred while fetching admin stats. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (role === 'admin') fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  if (role !== 'admin') return null;

  const newUsersChart = countByDate(newUsersList, STATS_WINDOW_DAYS);
  const activeUsersChart = countUniqueMobileByDate(activeUsersList, STATS_WINDOW_DAYS);
  const activeUsersTotal = uniqueMobiles(activeUsersList).length;

  return (
    <div>
      <Navbar />
      <div className="form-container" style={{ marginTop: '100px', maxWidth: '900px' }}>
        <div style={{ position: 'relative', textAlign: 'center', marginBottom: '10px' }}>
          <h2 style={{ display: 'inline-block', margin: 0 }}>Admin Dashboard</h2>
          <button
            onClick={fetchStats}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: 0,
              paddingLeft: '10px',
              position: 'relative',
              top: '1px',
            }}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCcw size={16} color="#094268" />
          </button>
        </div>

        {isLoading && (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        )}

        {errorMessage && (
          <div className="error-popup" style={{ border: '2px solid white', backgroundColor: 'white', padding: '20px', borderRadius: '8px', textAlign: 'center', maxWidth: '400px', margin: '20px auto' }}>
            <div className="error-message" style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>{errorMessage}</div>
            <button
              className="submit-button"
              onClick={fetchStats}
              style={{ padding: '10px 20px', fontSize: '14px' }}
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !errorMessage && (
          <>
            <div style={{ marginTop: '30px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <h3 style={{ margin: 0 }}>
                  New Users <span style={{ color: '#094268', fontWeight: 700 }}>({newUsersList.length})</span>
                </h3>
                <button
                  className="submit-button"
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                  onClick={() => downloadMobileList(uniqueMobiles(newUsersList), 'new_users.txt')}
                  disabled={newUsersList.length === 0}
                >
                  Download New Users
                </button>
              </div>
              <BarChart data={newUsersChart} color="#094268" />
            </div>

            <div style={{ marginTop: '40px', marginBottom: '40px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <h3 style={{ margin: 0 }}>
                  Active Users <span style={{ color: '#094268', fontWeight: 700 }}>({activeUsersTotal})</span>
                </h3>
                <button
                  className="submit-button"
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                  onClick={() => downloadMobileList(uniqueMobiles(activeUsersList), 'active_users.txt')}
                  disabled={activeUsersList.length === 0}
                >
                  Download Active Users
                </button>
              </div>
              <BarChart data={activeUsersChart} color="#f37021" />
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminPage;
